import { FC, useCallback, useEffect, useMemo } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

import { MLInput } from 'components/Input/Input';
import { ClientUpdateModalFormValues, clientUpdateModalSchema } from 'api/clients/types';
import useGetClient from 'hooks/Clients/useGetClient';
import useUpdateClient from 'hooks/Clients/useUpdateClient';
import useGetClientOwners from 'hooks/Clients/useGetClientOwners';
import { MLSelect } from 'components/Select/Select';
import { countries } from 'api/clients/country-list';

interface UpdateClientModalProps {
  clientId?: number;
  isOpen: boolean;
  onClose: () => void;
  refetchClients: () => void;
}

const UpdateClientModal: FC<UpdateClientModalProps> = ({ clientId, isOpen, onClose, refetchClients }) => {
  const { data } = useGetClient(clientId || 0);
  const { clientOwners } = useGetClientOwners();
  const { updateClient } = useUpdateClient();
  const { t } = useTranslation();

  const { handleSubmit, control, reset, setError } = useForm<ClientUpdateModalFormValues>({
    resolver: zodResolver(clientUpdateModalSchema),
    defaultValues: {
      name: '',
      EDRPOU: '',
      city: '',
      street: '',
      building_no: '',
      apartment_no: '',
      owner: '',
      image: null,
      technician: undefined,
      manager: null,
    },
  });

  const ownerOptions =
    !clientOwners || clientOwners.length === 0
      ? [{ value: '', label: 'No Owners to set' }]
      : [
          { value: '', label: t('Modals.manage-object.select-owner') },
          ...clientOwners.map(owner => ({
            value: owner.id.toString(),
            label: owner.last_name ? `${owner.last_name} ${owner.first_name}` : owner.email,
          })),
        ];
  const countryList = useMemo(() => {
    if (countries.length === 0 || !countries) {
      return [{ value: '', label: t('Modals.dont-have-country') }];
    }
    return [{ value: '', label: t('Modals.country-select') }].concat(
      countries.map(country => {
        return {
          value: String(country.id),
          label: country.countryName,
          flagImg: country.flagImg,
        };
      }),
    );
  }, [t]);
  const formatCountryOption = (option: any) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {option.flagImg && (
        <img src={option.flagImg} alt={option.label} style={{ width: '20px', height: '15px', display: 'block' }} />
      )}
      <span>{option.label}</span>
    </div>
  );
  const onReset = useCallback(() => {
    let countryId = '';
    if (data && clientId) {
      if (data?.country) {
        const foundCountry = countries.find(c => c.countryName === data.country);
        if (foundCountry) {
          countryId = String(foundCountry.id);
        }
      }
      reset({
        name: data.name || '',
        EDRPOU: data.EDRPOU || '',
        city: data.city || '',
        country: countryId,
        zip_code: data.zip_code || '',
        business_structure: data.business_structure || '',
        street: data.street || '',
        building_no: data.building_no || '',
        apartment_no: data.apartment_no || '',
        owner: data.owner?.toString() || '',
        image: data?.image || null,
        technician: undefined,
        manager: null,
      });
    }
  }, [data, clientId, reset]);

  const onUpdateClient = (formData: ClientUpdateModalFormValues) => {
    let payload = {
      ...formData,
      country: formData.country ? countryList.find(c => c.value === formData.country)?.label : '',
    };
    updateClient(
      {
        id: clientId,
        client: payload,
      },
      {
        onSuccess: () => {
          enqueueSnackbar(t('Modals.success-edit'), { variant: 'success' });
          onReset();
          onClose();
          refetchClients();
        },
        onError: (error: any) => {
          if (error.response?.data?.name) {
            setError('name', { message: 'Name already exists' });
          }
          if (error.response?.data?.EDRPOU) {
            setError('EDRPOU', { message: 'EDRPOU length should be not larger than 10' });
          }
        },
      },
    );
  };

  useEffect(() => {
    if (data && clientId) {
      onReset();
    }
  }, [data, clientId, onReset]);

  return (
    <div>
      <Modal
        isOpen={isOpen}
        toggle={() => {
          onReset();
          onClose();
        }}
        size="lg"
      >
        <ModalHeader
          className="border-bottom py-3 "
          toggle={() => {
            onReset();
            onClose();
          }}
        >
          <div className="text-purple">{t('Modals.manage-client.edit.title')}</div>
        </ModalHeader>
        <ModalBody className="mt-3">
          <div className="">
            <div className="row">
              <div className="mb-3 col-md-4">
                <Controller
                  control={control}
                  name="name"
                  render={({ field, fieldState: { error } }) => (
                    <MLInput
                      type="text"
                      label={t('Modals.manage-client.name')}
                      error={error?.message}
                      required
                      {...field}
                    />
                  )}
                />
              </div>

              <div className="mb-3 col-md-4">
                <Controller
                  control={control}
                  name="EDRPOU"
                  render={({ field, fieldState: { error } }) => (
                    <MLInput label={t('Modals.manage-client.EDRPOU')} type="text" error={error?.message} {...field} />
                  )}
                />
              </div>

              <div className="mb-3 col-md-4">
                <Controller
                  control={control}
                  name="business_structure"
                  render={({ field, fieldState: { error } }) => (
                    <MLInput
                      label={t('Modals.manage-client.business_structure')}
                      type="text"
                      error={error?.message}
                      {...field}
                    />
                  )}
                />
              </div>

              <div className="mb-3 col-md-4">
                <Controller
                  control={control}
                  name="country"
                  render={({ field, fieldState: { error } }) => (
                    <MLSelect
                      {...field}
                      label={t('Modals.manage-client.country')}
                      error={error?.message}
                      options={countryList}
                      formatOptionLabel={formatCountryOption}
                      value={String(field.value || '')}
                      onChange={val => field.onChange(val || '')}
                    />
                  )}
                />
              </div>

              <div className="mb-3 col-md-4">
                <Controller
                  control={control}
                  name="city"
                  render={({ field, fieldState: { error } }) => (
                    <MLInput {...field} error={error?.message} label={t('Modals.manage-client.city')} />
                  )}
                />
              </div>

              <div className="mb-3 col-md-4">
                <Controller
                  control={control}
                  name="zip_code"
                  render={({ field, fieldState: { error } }) => (
                    <MLInput {...field} error={error?.message} label={t('Modals.manage-client.zip')} />
                  )}
                />
              </div>

              <div className="mb-3 col-md-4">
                <Controller
                  control={control}
                  name="street"
                  render={({ field, fieldState: { error } }) => (
                    <MLInput {...field} error={error?.message} label={t('Modals.manage-client.street')} />
                  )}
                />
              </div>

              <div className="mb-3 col-md-4">
                <Controller
                  control={control}
                  name="building_no"
                  render={({ field, fieldState: { error } }) => (
                    <MLInput {...field} error={error?.message} label={t('Modals.manage-client.building_no')} />
                  )}
                />
              </div>

              <div className="mb-3 col-md-4">
                <Controller
                  control={control}
                  name="apartment_no"
                  render={({ field, fieldState: { error } }) => (
                    <MLInput label={t('Modals.manage-client.apartment_no')} error={error?.message} {...field} />
                  )}
                />
              </div>

              {/* Owner Selection - Only select, no creation */}
              <div className="mb-3 col-md-12">
                <Controller
                  control={control}
                  name="owner"
                  rules={{ required: true }}
                  render={({ field, formState: { errors } }) => (
                    <div>
                      <label className="form-label mb-1">
                        Owner<span className="text-danger m-1">*</span>
                      </label>
                      <Select
                        {...field}
                        required
                        options={ownerOptions}
                        value={ownerOptions.find(option => option.value === field.value) || null}
                        onChange={selectedOption => field.onChange(selectedOption ? selectedOption.value : '')}
                        placeholder={t('Modals.manage-client.owner')}
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            borderColor: errors?.owner?.message ? 'red' : base.borderColor,
                            boxShadow: state.isFocused ? '0 0 0 2px rgba(95, 0, 204, 0.5)' : 'none',
                          }),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isSelected
                              ? '#7700ff'
                              : state.isFocused
                                ? '#b683e6'
                                : base.backgroundColor,
                            color: state.isSelected || state.isFocused ? 'white' : 'black',
                          }),
                          singleValue: base => ({
                            ...base,
                            color: 'black',
                          }),
                        }}
                      />
                      {errors.owner && <p className="text-danger">Required field</p>}
                    </div>
                  )}
                />
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            className="btn btn-ghost-danger"
            onClick={() => {
              onReset();
              onClose();
            }}
          >
            {t('Modals.delete-modal.cancel')}
          </Button>
          <Button className="btn btn-ghost-success" onClick={handleSubmit(onUpdateClient)}>
            {t('Modals.manage-client.edit.title')}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default UpdateClientModal;
